export const SURVEY_PASSING_CONFIGS = {
  'emotional-dynamics': {
    title: 'Мониторинг эмоциональной динамики',
    questionsCount: 5,
    duration: '10 минут',
    welcome: {
      greeting:
          'Здравствуйте! Предлагаем вам решить 4 задания и выбрать наиболее подходящий ответ.',
      anonymityNote:
          'Ваши ответы анонимны. HR-специалист увидит только общие данные по компании или отделам, но не персональные результаты.',
    },
    steps: [
      {
        id: 'emotions-week',
        type: 'emotions',
        title: 'Выберите от 2 до 5 эмоций, которые вы испытывали на этой неделе',
        min: 2,
        max: 5,
      },
      {
        id: 'energy-level',
        type: 'energy',
        title: 'Отметьте свой уровень энергии на текущей неделе',
        hint: 'Потяните ползунок или введите значение от 0 до 100',
        fieldLabel: 'Уровень энергии',
      },
      {
        id: 'emotions-repeat',
        type: 'emotions',
        title: 'Выберите от 2 до 5 эмоций, которые вы испытывали на этой неделе',
        min: 2,
        max: 5,
        showSelectionHint: true,
      },
    ],
  },
  'soft-skills-growth': {
    title: 'Динамика роста гибких навыков',
    questionsCount: 5,
    duration: '10 минут',
    welcome: {
      greeting:
          'Здравствуйте! Предлагаем вам пройти опрос и оценить, насколько вы применяете эмоциональный интеллект в работе.',
      anonymityNote:
          'Ваши ответы анонимны. HR-специалист увидит только общие данные по компании или отделам, но не персональные результаты.',
    },
    steps: [
      {
        id: 'soft-skills-scale',
        type: 'scale',
        title:
            'Оцените по шкале от 1 до 9 (где 1 — никогда не применяю, а 9 — применяю постоянно), насколько у вас получается применять в деятельности описанное в утверждениях ниже:',
        statements: [
          'Управляю своими эмоциями в сложных ситуациях',
          'Лучше справляюсь со сложными ситуациями',
          'Общаюсь с коллегами более конструктивно',
          'Решаю конфликты и недопонимания быстрее',
          'Планирую работу с учётом ресурсов команды',
          'Увеличилась эффективность совещаний',
          'Решения принимаем быстрее и слаженнее',
        ],
      },
      {
        id: 'admiration-radio',
        type: 'radio',
        title:
            'Необходимо создать новый, необычный интерьер для проведения новогоднего корпоратива. В какой степени {highlight} могла бы этому помочь?',
        highlight: 'эмоция восхищения',
        groupTitle: 'Принятие',
        options: [
          'Совсем не помогает',
          'Слабо',
          'Средне',
          'Сильно',
          'Очень помогает',
        ],
      },
    ],
  },
};

export const getSurveyPassingConfig = (surveyId) => {
  return SURVEY_PASSING_CONFIGS[surveyId] ?? null;
};

const sortByPosition = (items) => {
  return [...items].sort((first, second) => {
    return (first.position ?? 0) - (second.position ?? 0);
  });
};

const getSavedAnswers = (surveyData) => {
  const rawAnswers = surveyData.questions.flatMap((question) => {
    return question.saved_answers.map((answer) => ({
      ...answer,
      question_id: question.id,
    }));
  });

  return rawAnswers.reduce((result, answer) => {
    const questionId = answer.question_id;

    if (!result[questionId]) {
      result[questionId] = [];
    }

    result[questionId].push(answer);

    return result;
  }, {});
};

const getSingleSavedValue = (savedAnswers, questionId) => {
  const answer = savedAnswers?.[questionId]?.[0];

  if (!answer) {
    return undefined;
  }

  if (answer.answer_id !== undefined && answer.answer_id !== null) {
    return answer.answer_id;
  }

  return answer.value;
};

const getEmotionSavedValue = (savedAnswers, questionId) => {
  const answers = savedAnswers?.[questionId] ?? [];

  if (!answers.length) {
    return undefined;
  }

  return sortByPosition(answers)
      .map((answer) => answer.value);
};

const mapAnswerChoiceQuestion = (question, savedAnswers) => ({
  id: `question-${question.id}`,
  questionId: question.id,
  type: 'radio',
  title: question.text,
  options: sortByPosition(question.answers).map((answer) => ({
    id: answer.id,
    value: answer.id,
    text: answer.text,
  })),
  initialValue: getSingleSavedValue(savedAnswers, question.id),
});

const mapAnswerChoiceStatementQuestion = (question, savedAnswers) => ({
  ...mapAnswerChoiceQuestion(question, savedAnswers),
  type: 'radio',
});

const mapNumericQuestion = (question, savedAnswers) => ({
  id: `question-${question.id}`,
  questionId: question.id,
  type: 'energy',
  title: question.text,
  hint: 'Потяните ползунок или введите значение от 0 до 100',
  fieldLabel: 'Уровень энергии',
  min: 0,
  max: 100,
  initialValue: getSingleSavedValue(savedAnswers, question.id),
});

const mapEmotionQuestion = (question, savedAnswers, emotionOptions) => ({
  id: `question-${question.id}`,
  questionId: question.id,
  type: 'emotions',
  title: question.text,
  min: 1,
  max: 5,
  options: emotionOptions,
  initialValue: getEmotionSavedValue(savedAnswers, question.id),
});

const createStatementStepFromQuestions = (questions, savedAnswers, surveyId) => ({
  id: `statements-${surveyId}`,
  type: 'scale',
  statementSubmitMode: 'questions',
  title: 'Оцените по шкале от 1 до 9 (где 1 — никогда не применяю, а 9 — применяю постоянно), насколько у вас получается применять в деятельности описанное в утверждениях ниже:\n',
  statements: questions.map((question) => ({
    id: question.id,
    questionId: question.id,
    text: question.text,
  })),
  min: 1,
  max: 9,
  initialValue: questions.map((question) => {
    const value = getSingleSavedValue(savedAnswers, question.id);

    return value === undefined ? null : Number(value);
  }),
});

const mapQuestionsToSteps = (questions, savedAnswers, emotionOptions, surveyId) => {
  const sortedQuestions = sortByPosition(questions);
  const statementQuestions = sortedQuestions.filter((question) => question.type === 'statement');
  const steps = sortedQuestions
      .filter((question) => question.type !== 'statement')
      .map((question) => {
        switch (question.type) {
          case 'numeric':
            return mapNumericQuestion(question, savedAnswers);
          case 'emotion':
            return mapEmotionQuestion(question, savedAnswers, emotionOptions);
          case 'answer_choice':
            return mapAnswerChoiceQuestion(question, savedAnswers);
          case 'answer_choice_statement':
            return mapAnswerChoiceStatementQuestion(question, savedAnswers);
          default:
            return null;
        }
      })
      .filter(Boolean);

  if (statementQuestions.length) {
    steps.push(createStatementStepFromQuestions(statementQuestions, savedAnswers, surveyId));
  }

  return steps;
};

export const createSurveyPassingConfig = (surveyData, emotionOptions = []) => {
  if (!surveyData?.survey) {
    return null;
  }

  const savedAnswers = getSavedAnswers(surveyData);
  const steps = mapQuestionsToSteps(
      surveyData.questions,
      savedAnswers,
      emotionOptions,
      surveyData.survey.id,
  );

  return {
    title: surveyData.survey.name,
    period: surveyData.survey.period,
    code: surveyData.code,
    questionsCount: steps.length,
    duration: surveyData.survey.period === 'weekly' ? '5 минут' : '10 минут',
    valid_until: surveyData.valid_until,
    welcome: {
      greeting: 'Здравствуйте! Пожалуйста, ответьте на вопросы опроса.',
      anonymityNote:
          'Ваши ответы анонимны. HR-специалист увидит только общие данные по компании или отделам.',
    },
    steps,
  };
};
